import { Link as RouterLink } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Portal,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { Ghost, Settings, LogOut, Key } from "lucide-react";
import { formatBytes, formatAccountExpiration } from "@core/utils/format";
import { SignOutConfirmModal } from "@/components/layout/headerComponents";
import type { UsageInfo } from "@/lib/api/keys";

interface SidebarUserMenuProps {
  hasApiKey: boolean;
  isCollapsed: boolean;
  displayName: string;
  avatarColor: string;
  usage: UsageInfo | null;
  accountExpiresAt: number | null;
  onSignUpOpen: () => void;
  onSignInOpen: () => void;
  onSignOutComplete: () => void;
}

export function SidebarUserMenu({
  hasApiKey,
  isCollapsed,
  displayName,
  avatarColor,
  usage,
  accountExpiresAt,
  onSignUpOpen,
  onSignInOpen,
  onSignOutComplete,
}: SidebarUserMenuProps) {
  const { colorMode } = useColorMode();
  const { isOpen: isSignOutOpen, onOpen: onSignOutOpen, onClose: onSignOutClose } = useDisclosure();

  if (hasApiKey) {
    return (
      <Menu placement="top-start">
        <MenuButton
          as={Button}
          variant={isCollapsed ? "unstyled" : "ghost"}
          size="sm"
          w={isCollapsed ? "auto" : "full"}
          justifyContent={isCollapsed ? "center" : "flex-start"}
          px={isCollapsed ? 1 : 2}
          py={isCollapsed ? 1 : 2}
          h="auto"
          borderRadius={isCollapsed ? "full" : "md"}
          minW={isCollapsed ? "auto" : undefined}
          transition="transform 0.15s ease"
          _hover={isCollapsed ? { transform: "scale(1.1)" } : undefined}
        >
          <HStack spacing={2} overflow="hidden">
            <Avatar
              size="xs"
              bg={avatarColor}
              icon={<Icon as={Ghost} boxSize={3} color="white" />}
              flexShrink={0}
            />
            {!isCollapsed && (
              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {displayName}
              </Text>
            )}
          </HStack>
        </MenuButton>
        <Portal>
          <MenuList minW="220px" maxW="300px">
            {/* User info header */}
            <HStack px={3} py={3} spacing={3}>
              <Avatar
                size="md"
                bg={avatarColor}
                icon={<Icon as={Ghost} boxSize={5} color="white" />}
                flexShrink={0}
              />
              <VStack align="start" spacing={0.5} overflow="hidden">
                <HStack spacing={1.5}>
                  <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                    {displayName}
                  </Text>
                  {usage && (
                    <Badge
                      size="xs"
                      colorScheme={usage.tier === "pro" ? "purple" : "gray"}
                      fontSize="9px"
                      textTransform="uppercase"
                      flexShrink={0}
                    >
                      {usage.tier}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="stone.500">
                  (anonymous)
                </Text>
                {formatAccountExpiration(accountExpiresAt) && (
                  <Text fontSize="xs" color="orange.500">
                    Expires in {formatAccountExpiration(accountExpiresAt)}
                  </Text>
                )}
              </VStack>
            </HStack>
            {/* Usage stats */}
            {usage && (
              <>
                <Box px={3} py={2}>
                  <VStack align="stretch" spacing={2}>
                    <Box>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" color="stone.500">
                          Shares
                        </Text>
                        <Text fontSize="xs" color="stone.500">
                          {usage.activeShares} / {usage.maxShares < 0 ? "\u221E" : usage.maxShares}
                        </Text>
                      </HStack>
                      <Box
                        h="4px"
                        bg={colorMode === "dark" ? "stone.700" : "stone.200"}
                        borderRadius="full"
                        overflow="hidden"
                      >
                        {usage.maxShares >= 0 && (
                          <Box
                            h="full"
                            w={`${Math.min((usage.activeShares / usage.maxShares) * 100, 100)}%`}
                            bg={
                              usage.activeShares / usage.maxShares > 0.8
                                ? "orange.400"
                                : "brand.500"
                            }
                            borderRadius="full"
                            transition="width 0.3s ease"
                          />
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" color="stone.500">
                          Storage
                        </Text>
                        <Text fontSize="xs" color="stone.500">
                          {formatBytes(usage.storageUsed)} /{" "}
                          {formatBytes(usage.maxStorage)}
                        </Text>
                      </HStack>
                      <Box
                        h="4px"
                        bg={colorMode === "dark" ? "stone.700" : "stone.200"}
                        borderRadius="full"
                        overflow="hidden"
                      >
                        <Box
                          h="full"
                          w={`${Math.min((usage.storageUsed / usage.maxStorage) * 100, 100)}%`}
                          bg={
                            usage.storageUsed / usage.maxStorage > 0.8
                              ? "orange.400"
                              : "brand.500"
                          }
                          borderRadius="full"
                          transition="width 0.3s ease"
                        />
                      </Box>
                    </Box>
                  </VStack>
                </Box>
                <MenuDivider />
              </>
            )}
            <MenuItem
              as={RouterLink}
              to="/settings"
              icon={<Icon as={Settings} boxSize={4} />}
            >
              Settings
            </MenuItem>
            <MenuDivider />
            <MenuItem
              icon={<Icon as={LogOut} boxSize={4} />}
              onClick={onSignOutOpen}
              color="red.500"
            >
              Sign out
            </MenuItem>
          </MenuList>
        </Portal>
        <SignOutConfirmModal
          isOpen={isSignOutOpen}
          onClose={onSignOutClose}
          onSignOut={onSignOutComplete}
        />
      </Menu>
    );
  }

  if (isCollapsed) {
    return (
      <Tooltip label="Sign in" placement="right">
        <IconButton
          aria-label="Sign in"
          icon={<Icon as={Key} boxSize={4} />}
          size="sm"
          variant="ghost"
          onClick={onSignInOpen}
        />
      </Tooltip>
    );
  }

  return (
    <VStack spacing={2} w="full">
      <Button size="sm" colorScheme="brand" onClick={onSignUpOpen} w="full">
        Try Anonymously
      </Button>
      <Button size="sm" variant="outline" onClick={onSignInOpen} w="full">
        Sign in with secret key
      </Button>
    </VStack>
  );
}
